import { randomUUID } from 'expo-crypto';
import { useDonations } from 'features/cart/hooks/use-donations';
import { useMinimalCart } from 'features/cart/hooks/use-minimal-cart';
import { commonStyles } from 'features/cart/styles';
import { ICartEntry, MinimalCartEntryType } from 'features/cart/types';
import { useIntl } from 'react-intl';
import { StyleSheet, Text, View } from 'react-native';

import { useFeatureDonation } from 'hooks/use-feature-donation';
import { StyledToggleButtonGroup } from 'pages/cart/donation-at-checkout/styled';
import { useUIContext } from 'state/ui';
import { emptySanityImage } from 'utils/sanity';

import { CartSectionHeader } from '../cart-section-header';

export const DonationsSection = () => {
  const { featureDonation } = useFeatureDonation();
  const { donations, loading } = useDonations();
  const { formatCurrencyForLocale } = useUIContext();
  const { cartEntries, addItemToMinimalCart, removeItemFromMinimalCart } = useMinimalCart();
  const { formatMessage } = useIntl();
  const selectDonationAmountText = formatMessage({ id: 'selectDonationAmount' });
  const selectedDonation: ICartEntry | undefined = cartEntries.find(
    entry => entry.type === MinimalCartEntryType.donation
  );

  const donationOptions = donations.map(donation => ({
    label: formatCurrencyForLocale(donation.price),
    value: donation.id,
    isDisabled: false,
  }));

  const handleChange = (value: string) => {
    if (selectedDonation?.id === value) {
      removeItemFromMinimalCart(selectedDonation);
      return;
    }
    const donation = donations.find(donation => donation.id === value);
    if (!donation) {
      return;
    }
    if (selectedDonation && donation.id !== selectedDonation.id) {
      removeItemFromMinimalCart(selectedDonation);
    }
    addItemToMinimalCart({
      id: value,
      cartId: randomUUID(),
      quantity: 1,
      name: donation.name,
      price: {
        amount: donation.price,
        currency: 'USD',
      },
      image: emptySanityImage(),
      url: '',
      type: MinimalCartEntryType.donation,
      children: [],
    });
  };

  return (
    <View style={styles.container}>
      <CartSectionHeader
        title={featureDonation?.header?.locale ?? ''}
        testID="donations-section-heading-label"
      />
      <Text style={commonStyles.cartEntryBody}>{featureDonation?.subtitle?.locale ?? ''}</Text>
      <StyledToggleButtonGroup
        options={donationOptions}
        onChangeValue={handleChange}
        value={selectedDonation?.id}
        accessibilityHint={selectDonationAmountText}
        accessibilityLabel={selectDonationAmountText}
        isDisabled={loading}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 16,
    backgroundColor: Styles.color.cardBackground,
    borderRadius: Styles.borderRadius,
    overflow: 'hidden',
  },
});
